/**
 * ASCII Hero Engine - AsciiDustGlyph Class
 * Ambient Floating ASCII Dust Glyph (with authentic Stop-Motion kinematics)
 */

import { DEFAULT_DUST_CHARS } from "../constants.js";

export class AsciiDustGlyph {
  constructor(width, height, isInitial = false, config = null) {
    this.config = config;
    this.reset(width, height, isInitial);
  }

  reset(width, height, isInitial = false) {
    const w = width || (typeof window !== "undefined" ? window.innerWidth : 1920);
    const h = height || (typeof window !== "undefined" ? window.innerHeight : 1080);

    // Continuous full-screen distribution
    this.x = w * Math.random();
    this.y = h * Math.random();
    this.renderX = this.x;
    this.renderY = this.y;

    // Gentle ambient drift
    this.baseVx = -(0.15 + Math.random() * 0.45);
    this.baseVy = (Math.random() - 0.5) * 0.3;
    this.vx = this.baseVx;
    this.vy = this.baseVy;
    this.depth = 0.15 + Math.random() * 0.85;

    this.baseAlpha = 0.12 + Math.random() * 0.32;
    this.alpha = this.baseAlpha;
    this.renderAlpha = this.baseAlpha;

    const pool = this.config && this.config.dustChars ? this.config.dustChars : DEFAULT_DUST_CHARS;
    this.char = pool[Math.floor(Math.random() * pool.length)];
    this.changeInterval = 25 + Math.floor(Math.random() * 60);
    this.changeTimer = 0;

    this.wobbleSpeed = 0.015 + Math.random() * 0.025;
    this.wobbleAmp = 0.25 + Math.random() * 0.45;
    this.wobblePhase = Math.random() * Math.PI * 2;

    // Stop-motion frame quantization state
    const dustFps = (this.config && this.config.dustFps) || 8;
    this.stepInterval = Math.max(1, Math.round(60 / dustFps));
    this.stepTimer = Math.floor(Math.random() * this.stepInterval);

    this.maxLife = 300 + Math.random() * 350;
    this.life = isInitial ? Math.random() * this.maxLife : 0;
    this.glitchColor = null;
  }

  update(mouse, width, height, config, parallaxX = 0, parallaxY = 0) {
    this.config = config;
    const dustSpeed = config && config.dustSpeed !== undefined ? config.dustSpeed : 1.0;
    const enableMouse = config && config.dustMouseInteraction !== undefined ? config.dustMouseInteraction : false;
    const mRadius = (config && config.dustMouseRadius) || 150;
    const mPower = config && config.dustMousePower !== undefined ? config.dustMousePower : 2.4;

    const isStopMotion = config && config.dustStopMotion !== undefined ? config.dustStopMotion : true;
    const dustFps = (config && config.dustFps) || 8;
    const stepInterval = Math.max(1, Math.round(60 / dustFps));
    const jitter = config && config.dustJitter !== undefined ? config.dustJitter : 1.6;

    this.life += dustSpeed;

    const dx = this.x - (mouse ? mouse.x : -9999);
    const dy = this.y - (mouse ? mouse.y : -9999);
    const dist = mouse && mouse.x !== -9999 ? Math.sqrt(dx * dx + dy * dy) : 99999;

    const isHovered = enableMouse && dist < mRadius && dist > 0;

    if (isHovered) {
      const force = (1 - dist / mRadius) * mPower;
      const angle = Math.atan2(dy, dx);
      this.vx += Math.cos(angle) * force;
      this.vy += Math.sin(angle) * force;
      this.alpha = Math.min(0.95, this.baseAlpha + (1 - dist / mRadius) * 0.65);

      if (Math.random() < 0.18) {
        const pool = config && config.dustChars ? config.dustChars : DEFAULT_DUST_CHARS;
        this.char = pool[Math.floor(Math.random() * pool.length)];
        if (config && config.glitchColorsRGB && Math.random() < (config.glitchColorChance || 0.45)) {
          this.glitchColor = config.glitchColorsRGB[Math.floor(Math.random() * config.glitchColorsRGB.length)];
        }
      }
    } else {
      this.alpha += (this.baseAlpha - this.alpha) * 0.04;
    }

    if (!isStopMotion) {
      this.vx += (this.baseVx - this.vx) * 0.04;
      this.vy += (this.baseVy - this.vy) * 0.04;

      this.x += this.vx;
      this.y += this.vy + Math.sin(this.life * this.wobbleSpeed + this.wobblePhase) * this.wobbleAmp;
      this.renderX = this.x;
      this.renderY = this.y;

      const rawFade = Math.sin((this.life / this.maxLife) * Math.PI);
      this.renderAlpha = this.alpha * rawFade;

      this.changeTimer += dustSpeed;
      if (this.changeTimer >= this.changeInterval) {
        this.changeTimer = 0;
        const pool = config && config.dustChars ? config.dustChars : DEFAULT_DUST_CHARS;
        this.char = pool[Math.floor(Math.random() * pool.length)];
        this.glitchColor = null;
      }
    } else {
      this.stepTimer += dustSpeed;

      if (this.stepTimer >= stepInterval) {
        this.stepTimer = 0;

        this.vx += (this.baseVx - this.vx) * 0.15;
        this.vy += (this.baseVy - this.vy) * 0.15;

        this.x += this.vx * stepInterval;
        this.y += (this.vy + Math.sin(this.life * this.wobbleSpeed + this.wobblePhase) * this.wobbleAmp) * stepInterval;

        const jX = (Math.random() - 0.5) * jitter;
        const jY = (Math.random() - 0.5) * jitter;
        this.renderX = Math.round(this.x + jX);
        this.renderY = Math.round(this.y + jY);

        const rawFade = Math.sin((this.life / this.maxLife) * Math.PI);
        const steppedFade = Math.round(rawFade * 8) / 8;
        this.renderAlpha = this.alpha * steppedFade;

        if (Math.random() < 0.22) {
          const pool = config && config.dustChars ? config.dustChars : DEFAULT_DUST_CHARS;
          this.char = pool[Math.floor(Math.random() * pool.length)];
          this.glitchColor = null;
        }
      }
    }

    if (this.life > this.maxLife || this.x < -40 || this.x > width + 40 || this.y < -40 || this.y > height + 40) {
      this.reset(width, height, false);
    }
  }

  draw(ctx, config, parallaxX = 0, parallaxY = 0) {
    const finalAlpha = Math.max(0, Math.min(1, this.renderAlpha));
    if (finalAlpha <= 0.001) return;

    const enableParallax = config && config.dustParallax !== undefined ? config.dustParallax : true;
    const pX = enableParallax ? parallaxX : 0;
    const pY = enableParallax ? parallaxY : 0;

    const renderX = this.renderX + pX * this.depth * 0.35;
    const renderY = this.renderY + pY * this.depth * 0.35;

    if (this.glitchColor) {
      const [r, g, b] = this.glitchColor;
      ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${Math.min(1, finalAlpha * 1.35)})`;
    } else {
      ctx.fillStyle = `rgba(255, 255, 255, ${finalAlpha})`;
    }
    ctx.fillText(this.char, renderX, renderY);
  }
}
